/**
 * ComuneAutocomplete - Fase 9B.1
 *
 * Dropdown autocomplete per comuni italiani / stati esteri usando
 * /api/utils/comuni e /api/utils/stati-esteri.
 *
 * Props:
 *  - value (string)
 *  - onChange (string) => void   (chiamato sia su typing sia su select)
 *  - onSelect (item) => void     (chiamato SOLO quando l'utente sceglie un'opzione)
 *  - placeholder (string)
 *  - includeEsteri (bool)        default true (mostra anche stati esteri se la query non matcha comuni)
 *  - disabled (bool)
 *  - id, name, required
 *  - className (string)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const DEBOUNCE_MS = 300;

export default function ComuneAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Comune di nascita",
  includeEsteri = true,
  disabled = false,
  id,
  name,
  required = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Click outside
  useEffect(() => {
    function handler(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = async (q) => {
    if (!q || q.trim().length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [comResp, estResp] = await Promise.all([
        axios.get(`${BACKEND}/api/utils/comuni`, { params: { q, limit: 10 } }).catch(() => ({ data: [] })),
        includeEsteri
          ? axios.get(`${BACKEND}/api/utils/stati-esteri`, { params: { q, limit: 5 } }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      const comuni = (comResp.data || []).map((c) => ({ ...c, kind: "comune" }));
      const esteri = (estResp.data || []).map((c) => ({ ...c, kind: "estero" }));
      setItems([...comuni, ...esteri]);
      setActiveIdx(-1);
    } catch (_e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    if (typeof onChange === "function") onChange(v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), DEBOUNCE_MS);
  };

  const pickItem = (item) => {
    if (!item) return;
    if (typeof onChange === "function") onChange(item.name);
    if (typeof onSelect === "function") onSelect(item);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIdx >= 0 && activeIdx < items.length) {
        e.preventDefault();
        pickItem(items[activeIdx]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = useMemo(() => open && (loading || items.length > 0 || (value || "").trim().length >= 2), [open, loading, items, value]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <input
        type="text"
        id={id}
        name={name}
        value={value || ""}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete="off"
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
        data-testid="comune-autocomplete-input"
      />
      {showDropdown && (
        <ul
          className="absolute z-50 mt-1 w-full max-h-64 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg"
          role="listbox"
          data-testid="comune-autocomplete-list"
        >
          {loading && (
            <li className="px-3 py-2 text-sm text-gray-500">Ricerca in corso…</li>
          )}
          {!loading && items.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">Nessun risultato</li>
          )}
          {!loading &&
            items.map((item, idx) => (
              <li
                key={`${item.kind}-${item.code}-${idx}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickItem(item);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between ${
                  idx === activeIdx ? "bg-blue-50" : "hover:bg-gray-50"
                }`}
                role="option"
                aria-selected={idx === activeIdx}
                data-testid="comune-autocomplete-item"
              >
                <span className="truncate">
                  <span className="font-medium">{item.name}</span>
                  {item.province && <span className="text-gray-500 ml-1">({item.province})</span>}
                  {item.kind === "estero" && (
                    <span className="text-xs text-purple-600 ml-2">estero</span>
                  )}
                  {item.active === false && (
                    <span className="text-xs text-orange-500 ml-2">soppresso</span>
                  )}
                </span>
                <span className="text-xs text-gray-400 font-mono">{item.code}</span>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
